import { useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import Dialog from 'src/componentes/dialog';
import Button from 'src/componentes/button';
import Panel from 'src/componentes/panel-collapse';
import { capitalizeFirstLetter } from 'src/utils/string';
import ItemList from 'src/paginas/item';
import { MODULOS, TIPO_ITENS } from 'src/utils/constants';
import useAuth from 'src/hooks/useAuth';

const ItensAssociadosList = ({
  model,
  habilitarIndicadoresRisco = false,
  showOnlyProjects = false,
  modules,
  ...rest
}) => {
  const { terms: resources } = useAuth();
  const modalRef = useRef();
  const intl = useIntl();

  const [indicadores, setIndicadores] = useState(
    model.value
      ? model.value.filter((i) => i.tipo.id === TIPO_ITENS.INDICADOR)
      : []
  );
  const [processos, setProcessos] = useState(
    model.value
      ? model.value.filter((i) => i.tipo.id === TIPO_ITENS.PROCESSO)
      : []
  );
  const [riscos, setRiscos] = useState(
    model.value ? model.value.filter((i) => i.tipo.id === TIPO_ITENS.RISCO) : []
  );
  const [projetos, setProjetos] = useState(
    model.value
      ? model.value.filter((i) => i.tipo.id === TIPO_ITENS.PROJETO)
      : []
  );
  const [objetivos, setObjetivos] = useState(
    model.value
      ? model.value.filter((i) => i.tipo.id === TIPO_ITENS.OBJETIVO_ESTRATEGICO)
      : []
  );
  const [indicadoresRisco, setIndicadoresRisco] = useState(
    model.value
      ? model.value.filter((i) => i.tipo.id === TIPO_ITENS.INDICADOR_RISCO)
      : []
  );

  const closeDialog = () => {
    if (modalRef?.current) modalRef.current.close();
  };

  const handleUpdateModel = () => {
    model.requestChange([
      ...indicadores,
      ...processos,
      ...riscos,
      ...projetos,
      ...objetivos,
      ...indicadoresRisco,
    ]);
  };

  const handleOkClick = () => {
    handleUpdateModel();
    closeDialog();
  };

  const handleCustomSelectSearch = async (selecteds, tipoId) => {
    const itens = selecteds || [];
    switch (tipoId) {
      case TIPO_ITENS.INDICADOR:
        setIndicadores(itens);
        break;
      case TIPO_ITENS.PROCESSO:
        setProcessos(itens);
        break;
      case TIPO_ITENS.RISCO:
        setRiscos(itens);
        break;
      case TIPO_ITENS.PROJETO:
        setProjetos(itens);
        break;
      case TIPO_ITENS.OBJETIVO_ESTRATEGICO:
        setObjetivos(itens);
        break;
      case TIPO_ITENS.INDICADOR_RISCO:
        setIndicadoresRisco(itens);
        break;
    }
  };

  const handleGetList = (tipoId) => {
    switch (tipoId) {
      case TIPO_ITENS.INDICADOR:
        return indicadores;
      case TIPO_ITENS.PROCESSO:
        return processos;
      case TIPO_ITENS.RISCO:
        return riscos;
      case TIPO_ITENS.PROJETO:
        return projetos;
      case TIPO_ITENS.OBJETIVO_ESTRATEGICO:
        return objetivos;
      case TIPO_ITENS.INDICADOR_RISCO:
        return indicadoresRisco;
      default:
        return [];
    }
  };

  const handleList = (tipoId, hierarchy = true) => {
    const list = handleGetList(tipoId);
    return (
      <ItemList
        multiple
        select
        selectRealTime
        selectedCallback={(itens) => handleCustomSelectSearch(itens, tipoId)}
        selectedItems={list}
        selectedIds={list}
        hierarchy={hierarchy}
        parameters={{ tipoId }}
      />
    );
  };

  const actions = [
    <Button key="cancel" onClick={closeDialog} color="secondary">
      {intl.formatMessage({ id: 'label.cancelar' })}
    </Button>,
    <Button key="save" className="ml-2" type="primary" onClick={handleOkClick}>
      {intl.formatMessage({ id: 'label.salvar' })}
    </Button>,
  ];

  const possuiModuloGE =
    !modules ||
    modules.some((m) => m.id === MODULOS.GESTAO_ESTRATEGICA && m.exibir);
  const possuiModuloMO =
    !modules ||
    modules.some((m) => m.id === MODULOS.MATRIZ_ORCAMENTARIA && m.exibir);
  const possuiModuloRV =
    !modules ||
    modules.some((m) => m.id === MODULOS.REMUNERACAO_VARIAVEL && m.exibir);
  const possuiModuloPROJETO =
    !modules ||
    modules.some((m) => m.id === MODULOS.ESCRITORIO_PROJETOS && m.exibir);
  const possuiGEMORV = possuiModuloGE || possuiModuloMO || possuiModuloRV;

  return (
    <Dialog
      ref={modalRef}
      width="600px"
      minWidth="80%"
      minHeight="50%"
      actions={actions}
      padContent
      title={capitalizeFirstLetter(resources.relacionamentos)}
      {...rest}
    >
      {possuiGEMORV && !showOnlyProjects && (
        <Panel header={capitalizeFirstLetter(resources.indicador)} open={false}>
          {handleList(TIPO_ITENS.INDICADOR)}
        </Panel>
      )}
      {!showOnlyProjects && (
        <Panel header={capitalizeFirstLetter(resources.processos)} open={false}>
          {handleList(TIPO_ITENS.PROCESSO)}
        </Panel>
      )}
      <Panel header={capitalizeFirstLetter(resources.risco)} open={false}>
        {handleList(TIPO_ITENS.RISCO, false)}
      </Panel>
      {(possuiModuloPROJETO || possuiGEMORV) && (
        <Panel header={capitalizeFirstLetter(resources.projeto)} open={false}>
          {handleList(TIPO_ITENS.PROJETO)}
        </Panel>
      )}
      {possuiGEMORV && !showOnlyProjects && (
        <Panel
          header={capitalizeFirstLetter(resources.objetivoestrategico)}
          open={false}
        >
          {handleList(TIPO_ITENS.OBJETIVO_ESTRATEGICO)}
        </Panel>
      )}
      {habilitarIndicadoresRisco && (
        <Panel
          header={capitalizeFirstLetter(
            intl.formatMessage(
              { id: 'indicadorRisco' },
              { risco: resources.risco }
            )
          )}
          open={false}
        >
          {handleList(TIPO_ITENS.INDICADOR_RISCO, false)}
        </Panel>
      )}
    </Dialog>
  );
};

export default ItensAssociadosList;
